/**
 * F14.2 — EL OPERARIO EJECUTA SUS TAREAS: UNA SOLA ENTRADA, EN LÍNEA Y SIN CONEXIÓN
 *
 * «Mis trabajos» llama siempre aquí; esta capa decide por dónde va la acción, nunca la pantalla:
 *
 *   1. Si la tarea ya tiene acciones guardadas en el dispositivo sin llegar al servidor, la nueva se ENCOLA detrás
 *      (mismo orden, misma cadena `dependeDe`): nunca adelanta en línea a una anterior que sigue pendiente.
 *   2. Con conexión, va directa al servidor (`POST /api/mis-ots/:idOt/lineas/:idLinea/<acción>`, la misma regla de
 *      dominio que la sincronización, modo PROPIA en el backend) con un `idOperacionCliente` nuevo.
 *   3. Si ese envío se queda sin respuesta (sin red, servidor caído, 502–504), la MISMA acción se encola con el
 *      MISMO `idOperacionCliente`: si el servidor llegó a aplicarla, la sincronización la reconoce (YA_APLICADA) y
 *      nunca hay dos ejecuciones.
 *   4. Sin conexión, se encola (`mutaciones.ts::crearMutacionLinea`: capacidad, caducidad, transición, REHACER).
 *
 * Un rechazo de negocio del servidor (tarea ya no asignada, transición no válida, falta el motivo…) se devuelve tal
 * cual: nunca se encola algo que el servidor ya dijo que no.
 *
 * El ejecutor real nunca viaja desde aquí: `ejecutoresReales: []` y el servidor pone al operario de la sesión.
 */
import { api, FalloApi } from '../api.ts';
import { actualizarLineaTrasSincronizacion, leerMutacion, listarMutacionesDeLinea, type PayloadMutacion, type TipoMutacionLinea } from './db.ts';
import { crearMutacionEvidencia, crearMutacionLinea } from './mutaciones.ts';
import { esMutacionActiva, esTipoMutacionLinea } from './proyeccion.ts';

export type AccionTarea = 'HECHO' | 'NO_CONFORME' | 'NO_APLICA' | 'REHACER';

const TIPO_DE_ACCION: Readonly<Record<AccionTarea, TipoMutacionLinea>> = {
  HECHO: 'LINEA_CONFIRMAR',
  NO_CONFORME: 'LINEA_NO_OK',
  NO_APLICA: 'LINEA_NO_APLICA',
  REHACER: 'LINEA_CONFIRMAR',
};

const RUTA_DE_ACCION: Readonly<Record<AccionTarea, string>> = {
  HECHO: 'confirmar',
  NO_CONFORME: 'no-ok',
  NO_APLICA: 'no-aplica',
  REHACER: 'confirmar',
};

/** Cómo se llama la acción en el centro de sincronización: «Confirmar limpieza · Habitación 305». */
export const ROTULO_ACCION: Readonly<Record<AccionTarea, string>> = {
  HECHO: 'Confirmar limpieza',
  NO_CONFORME: 'No conforme',
  NO_APLICA: 'No aplica',
  REHACER: 'Rehacer limpieza',
};

export interface ContextoEjecucion {
  /** Perfil offline DESBLOQUEADO de este usuario, o `null` si el dispositivo no está preparado. */
  readonly perfilId: string | null;
  readonly roles: readonly { readonly rol: string }[];
  /** Conexión real con el servidor (`pwa.ts::useConectividad`), nunca sólo `navigator.onLine`. */
  readonly enLinea: boolean;
}

export interface PeticionTarea {
  readonly idOt: string;
  readonly idLinea: string;
  readonly accion: AccionTarea;
  /** Obligatorio en NO_CONFORME y NO_APLICA. */
  readonly motivo: string | null;
  /** Obligatoria en REHACER (qué se ha corregido). */
  readonly observacion: string | null;
  /** Dónde, en palabras («Habitación 305»), para el rótulo de la acción. */
  readonly lugar: string;
  readonly fechaEfectiva?: string;
}

export type ResultadoTarea =
  | { readonly ok: true; readonly donde: 'SERVIDOR'; readonly estadoNuevo: string }
  | { readonly ok: true; readonly donde: 'DISPOSITIVO'; readonly aviso: string | null }
  | { readonly ok: false; readonly motivo: string };

export const MOTIVO_SIN_PREPARAR =
  'Sin conexión. Para guardar trabajo sin conexión, prepara antes este dispositivo desde «Mis trabajos → Preparar sin conexión».';

/** Sin respuesta del servidor: la acción puede haberse aplicado o no — sólo la sincronización lo sabrá. */
export function esFalloSinRespuesta(e: unknown): boolean {
  return e instanceof FalloApi && (e.codigo === 'SIN_CONEXION' || e.estado === 0 || (e.estado >= 502 && e.estado <= 504));
}

function payloadDe(p: PeticionTarea, fechaEfectiva: string): PayloadMutacion {
  const observacion = p.observacion !== null && p.observacion.trim() !== '' ? p.observacion.trim() : null;
  const tipo = TIPO_DE_ACCION[p.accion];
  if (tipo === 'LINEA_CONFIRMAR') return { fechaEfectiva, observacion, ejecutoresReales: [] };
  return { fechaEfectiva, observacion, motivo: (p.motivo ?? '').trim() };
}

async function tieneAccionesPendientes(perfilId: string, idLinea: string): Promise<boolean> {
  const deLinea = await listarMutacionesDeLinea(perfilId, idLinea);
  return deLinea.some((m) => esTipoMutacionLinea(m.tipo) && esMutacionActiva(m));
}

async function encolar(ctx: ContextoEjecucion & { perfilId: string }, p: PeticionTarea, fechaEfectiva: string, idOperacionCliente?: string): Promise<ResultadoTarea> {
  if (idOperacionCliente !== undefined && (await leerMutacion(ctx.perfilId, idOperacionCliente)) !== null) {
    return { ok: true, donde: 'DISPOSITIVO', aviso: null };
  }
  const r = await crearMutacionLinea(ctx.perfilId, ctx.roles, {
    tipo: TIPO_DE_ACCION[p.accion],
    idOt: p.idOt,
    idLinea: p.idLinea,
    payload: payloadDe(p, fechaEfectiva),
    fechaEfectiva,
    rotuloLocal: `${ROTULO_ACCION[p.accion]} · ${p.lugar}`,
    ...(idOperacionCliente === undefined ? {} : { idOperacionCliente }),
  });
  return r.ok ? { ok: true, donde: 'DISPOSITIVO', aviso: r.aviso ?? null } : { ok: false, motivo: r.motivo };
}

export async function ejecutarTareaPropia(ctx: ContextoEjecucion, p: PeticionTarea): Promise<ResultadoTarea> {
  const fechaEfectiva = p.fechaEfectiva ?? new Date().toISOString();
  const tipo = TIPO_DE_ACCION[p.accion];
  if (tipo !== 'LINEA_CONFIRMAR' && (p.motivo ?? '').trim() === '') return { ok: false, motivo: 'Indica el motivo.' };
  if (p.accion === 'REHACER' && (p.observacion ?? '').trim() === '') return { ok: false, motivo: 'Para rehacer, explica qué has corregido.' };

  const perfilId = ctx.perfilId;
  if (perfilId !== null && (await tieneAccionesPendientes(perfilId, p.idLinea))) {
    return encolar({ ...ctx, perfilId }, p, fechaEfectiva);
  }

  if (ctx.enLinea) {
    const idOperacionCliente = crypto.randomUUID();
    try {
      const r = await api.post<{ idEvento: string; estadoNuevo: string }>(
        `/api/mis-ots/${p.idOt}/lineas/${p.idLinea}/${RUTA_DE_ACCION[p.accion]}`,
        { ...payloadDe(p, fechaEfectiva), idOperacionCliente },
      );
      //  El paquete local (si lo hay) refleja lo que el servidor acaba de aceptar: la siguiente acción sin conexión
      //  parte del estado y la revisión reales.
      if (perfilId !== null) {
        await actualizarLineaTrasSincronizacion(perfilId, p.idLinea, { estado: r.estadoNuevo, revisionBase: r.idEvento }).catch(() => undefined);
      }
      return { ok: true, donde: 'SERVIDOR', estadoNuevo: r.estadoNuevo };
    } catch (e) {
      if (!esFalloSinRespuesta(e)) return { ok: false, motivo: e instanceof Error ? e.message : 'No se ha podido guardar.' };
      if (perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR };
      return encolar({ ...ctx, perfilId }, p, fechaEfectiva, idOperacionCliente);
    }
  }

  if (perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR };
  return encolar({ ...ctx, perfilId }, p, fechaEfectiva);
}

// ------------------------------------------------------------------ fotos

export interface PeticionFoto {
  readonly idOt: string;
  readonly idLinea: string;
  readonly archivo: File;
  readonly contexto: 'ANTES' | 'DESPUES' | 'GENERAL';
  readonly lugar: string;
}

export type ResultadoFoto =
  | { readonly ok: true; readonly donde: 'SERVIDOR' | 'DISPOSITIVO'; readonly aviso: string | null }
  | { readonly ok: false; readonly motivo: string };

/**
 * Foto de SU tarea: en línea por su ruta propia; sin respuesta o sin conexión, al almacén de evidencias de siempre
 * (comprimida, cifrada, con cuota) con el MISMO `idOperacionCliente`. Siempre INTERNA.
 */
export async function subirFotoTarea(ctx: ContextoEjecucion, p: PeticionFoto): Promise<ResultadoFoto> {
  const idOperacionCliente = crypto.randomUUID();
  if (ctx.enLinea) {
    try {
      const datos = new FormData();
      datos.set('contexto', p.contexto);
      datos.set('idOperacionCliente', idOperacionCliente);
      datos.set('archivo', p.archivo);
      await api.subir(`/api/mis-ots/${p.idOt}/lineas/${p.idLinea}/evidencias`, datos);
      return { ok: true, donde: 'SERVIDOR', aviso: null };
    } catch (e) {
      if (!esFalloSinRespuesta(e)) return { ok: false, motivo: e instanceof Error ? e.message : 'No se ha podido subir la foto.' };
    }
  }
  if (ctx.perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR };
  const ahora = new Date().toISOString();
  const r = await crearMutacionEvidencia(ctx.perfilId, ctx.roles, {
    ambito: 'LINEA',
    idLinea: p.idLinea,
    propia: true,
    idOt: p.idOt,
    contexto: p.contexto,
    descripcion: null,
    tomadaEn: ahora,
    visibilidad: 'INTERNA',
    archivo: p.archivo,
    nombreArchivo: p.archivo.name !== '' ? p.archivo.name : 'foto.jpg',
    fechaEfectiva: ahora,
    idOperacionCliente,
    rotuloLocal: `Foto · ${p.lugar}`,
  });
  return r.ok ? { ok: true, donde: 'DISPOSITIVO', aviso: r.aviso ?? null } : { ok: false, motivo: r.motivo };
}
